import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box, Text } from 'grommet';
import { Download } from 'grommet-icons';
import { AppButton, SignatureInput } from '@Components/Control';
import {
  PrivacyPolicyModal, TermsOfUseModal, CidOnboardingTermsModal, ExhibitAModal,
} from '@Components/Modal';
import { useAppTheme } from '@Components/Context';

const PrefillAgreementStep = ({
  small,
  submitLoading,
  basicDetails,
  terms,
  exhibitAData,
  onSignatureSubmit,
  onSignatureReset,
  signatureSubmitted,
  signaturePreview = null,
  finalSubmitted,
  isAgency = false,
  onDownloadAgreement = null,
  downloadingAgreement = false,
}) => {
  const { theme: { page, button } } = useAppTheme();

  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showAgreement, setShowAgreement] = useState(false);
  const [showExhibitA, setShowExhibitA] = useState(false);

  const isCustomAgreement = terms?.source === 'custom' && !!terms?.body;

  const decodedAgreementBody = useMemo(() => {
    if (!isCustomAgreement) return '';
    try {
      const bytes = Uint8Array.from(atob(terms.body), (c) => c.charCodeAt(0));
      const decoded = new TextDecoder().decode(bytes);
      return decoded.replace(/font-family\s*:[^;}]+;?/gi, '');
    } catch { return terms.body; }
  }, [isCustomAgreement, terms?.body]);

  // Treat as HTML if it starts with an HTML tag (editor output or .html upload)
  const isHtmlBody = isCustomAgreement && decodedAgreementBody.trimStart().startsWith('<');

  const isDetailsValid = !!(basicDetails?.companyName && basicDetails?.fullName && basicDetails?.email);

  const effectiveDate = new Date().toLocaleDateString(undefined, {
    year: 'numeric', month: 'long', day: 'numeric',
  });

  return (
    <Box gap="1rem">
      <Box gap="0.25rem">
        <Text weight={600} size="1.25rem" color={page.primaryText}>Agreement Acknowledgment</Text>
        <Text size="0.85rem" color={page.secondaryText}>
          Please review the agreement and provide your electronic signature to proceed.
        </Text>
      </Box>
      <Box
        gap="1rem"
        background={button.selectBg}
        round="0.5rem"
        pad="1.25rem"
        border={{ color: button.primaryBg, size: '1px' }}
      >
        <Text size="1.1rem" color={page.primaryText} style={{ lineHeight: '1.5' }}>
          By signing below, I acknowledge that I have reviewed and agree to
          {' '}
          <Text
            color={button.primaryBg}
            style={{ textDecoration: 'underline', cursor: 'pointer' }}
            onClick={() => setShowAgreement(true)}
          >
            Redflag&apos;s Content Protection Services Agreement
          </Text>
          , including
          {' '}
          <Text
            color={button.primaryBg}
            style={{ textDecoration: 'underline', cursor: 'pointer' }}
            onClick={() => setShowExhibitA(true)}
          >
            Exhibit A
          </Text>
          {' ('}
          <Text
            color={button.primaryBg}
            style={{ textDecoration: 'underline', cursor: 'pointer' }}
            onClick={() => setShowTerms(true)}
          >
            Terms & Conditions
          </Text>
          {' and '}
          <Text
            color={button.primaryBg}
            style={{ textDecoration: 'underline', cursor: 'pointer' }}
            onClick={() => setShowPrivacy(true)}
          >
            Privacy Policy
          </Text>
          ).
        </Text>
        <Box gap="0.25rem" background={page.cardBg} round="var(--app-radius)" pad="1rem">
          <Text weight={600} size="1.1rem" color={page.primaryText}>Agreement Copy</Text>
          <Text size="0.95rem" color={page.secondaryText}>
            A signed copy of this agreement, including all exhibits and terms,
            will be automatically sent to your provided email address after submission.
          </Text>
          {onDownloadAgreement && (
            <Box pad={{ top: '0.5rem' }}>
              <AppButton
                overrideHover
                level="tableControls"
                label={downloadingAgreement ? 'Generating...' : 'Download Agreement'}
                icon={!downloadingAgreement ? <Download size="small" /> : undefined}
                disabled={downloadingAgreement}
                bgColor={button.primaryBg}
                color={button.primaryText}
                onClick={onDownloadAgreement}
              />
            </Box>
          )}
        </Box>
      </Box>
      {/* Services Agreement — custom body if provided, otherwise standard */}
      {showAgreement && (isCustomAgreement ? (
        <Box
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onClick={() => setShowAgreement(false)}
        >
          <Box
            background={page.modalBg || page.cardBg}
            round="var(--app-radius)"
            pad="1.5rem"
            width={small ? '95vw' : '60vw'}
            style={{ maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}
            onClick={(e) => e.stopPropagation()}
          >
            <Box direction="row" justify="between" align="center" margin={{ bottom: '1rem' }}>
              <Text weight={600} size="1.25rem" color={page.primaryText}>
                Content Protection Services Agreement
              </Text>
              <AppButton
                overrideHover
                onClick={() => setShowAgreement(false)}
                level="tableControls"
                bgColor={button.primaryBg}
                color={button.primaryText}
                label="Close"
              />
            </Box>
            <Box style={{ overflowY: 'auto', flex: 1 }} pad={{ right: '0.5rem' }}>
              {isHtmlBody ? (
                <div
                  // Admin-authored HTML from the in-app editor or an uploaded .html file.
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{ __html: decodedAgreementBody }}
                  style={{ fontSize: '0.9rem', lineHeight: '1.6', color: page.primaryText }}
                />
              ) : (
                <Text
                  size="0.9rem"
                  color={page.primaryText}
                  style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6' }}
                >
                  {decodedAgreementBody}
                </Text>
              )}
            </Box>
          </Box>
        </Box>
      ) : (
        <CidOnboardingTermsModal
          toggle={() => setShowAgreement(false)}
          isSmall={small}
          currentYear={new Date().getFullYear()}
          background={page.modalBg}
          textColor={page.modalText}
          isAgency={isAgency}
        />
      ))}
      {showExhibitA && (
        <ExhibitAModal
          toggle={() => setShowExhibitA(false)}
          isSmall={small}
          prefillExhibitA={{
            products: exhibitAData?.products || [],
            extraItems: exhibitAData?.extraItems || [],
            currency: exhibitAData?.currency || 'USD',
            effectiveDate,
          }}
        />
      )}
      {showTerms && (
        <TermsOfUseModal
          toggle={() => setShowTerms(false)}
          isSmall={small}
          currentYear={new Date().getFullYear()}
          background={page.modalBg}
          textColor={page.modalText}
        />
      )}
      {showPrivacy && (
        <PrivacyPolicyModal
          toggle={() => setShowPrivacy(false)}
          isSmall={small}
          currentYear={new Date().getFullYear()}
          background={page.modalBg}
          textColor={page.modalText}
        />
      )}
      <SignatureInput
        small={small}
        submitLoading={submitLoading}
        signatureSubmitted={signatureSubmitted}
        signaturePreview={signaturePreview}
        finalSubmitted={finalSubmitted}
        isDetailsValid={isDetailsValid}
        onSignatureSubmit={onSignatureSubmit}
        onSignatureReset={onSignatureReset}
        uploadInputId="prefill-signature-upload-input"
      />
    </Box>
  );
};

PrefillAgreementStep.propTypes = {
  small: PropTypes.bool.isRequired,
  submitLoading: PropTypes.bool.isRequired,
  basicDetails: PropTypes.shape({
    email: PropTypes.string,
    fullName: PropTypes.string,
    companyName: PropTypes.string,
    phone: PropTypes.string,
    country: PropTypes.string,
  }).isRequired,
  terms: PropTypes.shape({
    source: PropTypes.oneOf(['default', 'custom']),
    body: PropTypes.string,
  }),
  exhibitAData: PropTypes.shape({
    products: PropTypes.arrayOf(PropTypes.shape({
      productKey: PropTypes.string,
      productLabel: PropTypes.string.isRequired,
      productType: PropTypes.string,
      plan: PropTypes.shape({ name: PropTypes.string, data: PropTypes.object }),
      config: PropTypes.object,
    })),
    extraItems: PropTypes.arrayOf(PropTypes.shape({
      kind: PropTypes.string.isRequired,
      valueType: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired,
      description: PropTypes.string.isRequired,
      frequency: PropTypes.string.isRequired,
      productKey: PropTypes.string,
    })),
    currency: PropTypes.string,
  }),
  onSignatureSubmit: PropTypes.func.isRequired,
  onSignatureReset: PropTypes.func.isRequired,
  signatureSubmitted: PropTypes.bool.isRequired,
  signaturePreview: PropTypes.string,
  finalSubmitted: PropTypes.bool.isRequired,
  isAgency: PropTypes.bool,
  onDownloadAgreement: PropTypes.func,
  downloadingAgreement: PropTypes.bool,
};

export default PrefillAgreementStep;
